// ============================================
// COMPONENT - Calculator Modal
// ============================================

import { useState, useEffect } from 'react';
import { MdClose } from 'react-icons/md';

interface CalculatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (value: number) => void;
}

type Operation = '+' | '-' | '*' | '/' | null;

export const CalculatorModal = ({ isOpen, onClose, onApply }: CalculatorModalProps) => {
  const [display, setDisplay] = useState('0');
  const [previousValue, setPreviousValue] = useState<number | null>(null);
  const [operation, setOperation] = useState<Operation>(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Reset al abrir
      setDisplay('0');
      setPreviousValue(null);
      setOperation(null);
      setWaitingForOperand(false);
    }
  }, [isOpen]);

  // Soporte de teclado
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Números
      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        inputDigit(e.key);
        return;
      }

      // Decimal
      if (e.key === '.' || e.key === ',') {
        e.preventDefault();
        inputDecimal();
        return;
      }

      // Operaciones
      if (e.key === '+') {
        e.preventDefault();
        performOperation('+');
        return;
      }
      if (e.key === '-') {
        e.preventDefault();
        performOperation('-');
        return;
      }
      if (e.key === '*') {
        e.preventDefault();
        performOperation('*');
        return;
      }
      if (e.key === '/') {
        e.preventDefault();
        performOperation('/');
        return;
      }

      // Igual (Enter)
      if (e.key === 'Enter' || e.key === '=') {
        e.preventDefault();
        if (e.key === 'Enter' && operation === null) {
          handleApply();
        } else {
          handleEquals();
        }
        return;
      }

      // Borrar último carácter (Backspace)
      if (e.key === 'Backspace') {
        e.preventDefault();
        deleteLastDigit();
        return;
      }

      // Limpiar todo (Escape o Delete)
      if (e.key === 'Escape' || e.key === 'Delete') {
        e.preventDefault();
        clear();
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, display, previousValue, operation, waitingForOperand]);

  const inputDigit = (digit: string) => {
    if (waitingForOperand) {
      setDisplay(digit);
      setWaitingForOperand(false);
    } else {
      setDisplay(display === '0' ? digit : display + digit);
    }
  };

  const inputDecimal = () => {
    if (waitingForOperand) {
      setDisplay('0.');
      setWaitingForOperand(false);
    } else if (display.indexOf('.') === -1) {
      setDisplay(display + '.');
    }
  };

  const clear = () => {
    setDisplay('0');
    setPreviousValue(null);
    setOperation(null);
    setWaitingForOperand(false);
  };

  const deleteLastDigit = () => {
    if (display.length > 1) {
      setDisplay(display.slice(0, -1));
    } else {
      setDisplay('0');
    }
  };

  const performOperation = (nextOperation: Operation) => {
    const inputValue = parseFloat(display);

    if (previousValue === null) {
      setPreviousValue(inputValue);
    } else if (operation) {
      const currentValue = previousValue || 0;
      let newValue = currentValue;

      switch (operation) {
        case '+':
          newValue = currentValue + inputValue;
          break;
        case '-':
          newValue = currentValue - inputValue;
          break;
        case '*':
          newValue = currentValue * inputValue;
          break;
        case '/':
          newValue = currentValue / inputValue;
          break;
      }

      setDisplay(String(newValue));
      setPreviousValue(newValue);
    }

    setWaitingForOperand(true);
    setOperation(nextOperation);
  };

  const handleEquals = () => {
    const inputValue = parseFloat(display);

    if (operation && previousValue !== null) {
      let result = previousValue;

      switch (operation) {
        case '+':
          result = previousValue + inputValue;
          break;
        case '-':
          result = previousValue - inputValue;
          break;
        case '*':
          result = previousValue * inputValue;
          break;
        case '/':
          result = previousValue / inputValue;
          break;
      }

      setDisplay(String(result));
      setPreviousValue(null);
      setOperation(null);
      setWaitingForOperand(true);
    }
  };

  const handleApply = () => {
    const value = parseFloat(display);
    if (!isNaN(value) && isFinite(value) && value > 0) {
      onApply(value);
      onClose();
    }
  };

  // Formatear número con separador de miles en formato español
  const formatDisplay = (value: string) => {
    // Si está escribiendo un decimal, no formatear
    if (value.endsWith('.')) return value;
    
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    
    // Formatear con separador de miles (punto) y decimal (punto también en este caso)
    return num.toLocaleString('es-ES', { 
      maximumFractionDigits: 10,
      useGrouping: true 
    });
  };

  if (!isOpen) return null;

  const buttonClass = "h-14 rounded-lg font-semibold text-lg transition-all hover:scale-105 active:scale-95";
  const numberButtonClass = `${buttonClass} bg-white hover:bg-gray-50 border-2`;
  const operationButtonClass = `${buttonClass} text-white`;
  const functionButtonClass = `${buttonClass} bg-gray-200 hover:bg-gray-300 border-2 border-gray-300`;
  const equalsButtonClass = "rounded-lg font-semibold text-lg transition-all hover:scale-105 active:scale-95 text-white";

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold" style={{ color: '#11194F' }}>
            Calculadora
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <MdClose className="text-2xl" />
          </button>
        </div>

        {/* Display */}
        <div className="px-6 py-6">
          <div
            className="rounded-xl px-6 py-6 text-right mb-4"
            style={{ backgroundColor: '#F3F4F9' }}
          >
            <div className="text-sm text-gray-500 mb-1 h-5">
              {operation && previousValue !== null
                ? `${formatDisplay(String(previousValue))} ${operation}`
                : ''}
            </div>
            <div className="text-4xl font-bold truncate" style={{ color: '#11194F' }}>
              {formatDisplay(display)}
            </div>
          </div>

          {/* Calculator Grid */}
          <div className="grid grid-cols-4 auto-rows-[56px] gap-2 mb-4">
            {/* First Row */}
            <button
              onClick={deleteLastDigit}
              className={functionButtonClass}
              style={{ color: '#11194F' }}
            >
              ⌫
            </button>
            <button
              onClick={clear}
              className={functionButtonClass}
              style={{ color: '#EF4444' }}
            >
              C
            </button>
            <button
              onClick={() => performOperation('/')}
              className={operationButtonClass}
              style={{ backgroundColor: '#43BFFF' }}
            >
              ÷
            </button>
            <button
              onClick={() => performOperation('*')}
              className={operationButtonClass}
              style={{ backgroundColor: '#43BFFF' }}
            >
              ×
            </button>

            {/* Numbers 7-9 and - button */}
            <button
              onClick={() => inputDigit('7')}
              className={numberButtonClass}
              style={{ color: '#11194F', borderColor: '#E5E7EB' }}
            >
              7
            </button>
            <button
              onClick={() => inputDigit('8')}
              className={numberButtonClass}
              style={{ color: '#11194F', borderColor: '#E5E7EB' }}
            >
              8
            </button>
            <button
              onClick={() => inputDigit('9')}
              className={numberButtonClass}
              style={{ color: '#11194F', borderColor: '#E5E7EB' }}
            >
              9
            </button>
            <button
              onClick={() => performOperation('-')}
              className={operationButtonClass}
              style={{ backgroundColor: '#43BFFF' }}
            >
              -
            </button>

            {/* Numbers 4-6 and + button */}
            <button
              onClick={() => inputDigit('4')}
              className={numberButtonClass}
              style={{ color: '#11194F', borderColor: '#E5E7EB' }}
            >
              4
            </button>
            <button
              onClick={() => inputDigit('5')}
              className={numberButtonClass}
              style={{ color: '#11194F', borderColor: '#E5E7EB' }}
            >
              5
            </button>
            <button
              onClick={() => inputDigit('6')}
              className={numberButtonClass}
              style={{ color: '#11194F', borderColor: '#E5E7EB' }}
            >
              6
            </button>
            <button
              onClick={() => performOperation('+')}
              className={operationButtonClass}
              style={{ backgroundColor: '#43BFFF' }}
            >
              +
            </button>

            {/* Numbers 1-3 and = button */}
            <button
              onClick={() => inputDigit('1')}
              className={numberButtonClass}
              style={{ color: '#11194F', borderColor: '#E5E7EB' }}
            >
              1
            </button>
            <button
              onClick={() => inputDigit('2')}
              className={numberButtonClass}
              style={{ color: '#11194F', borderColor: '#E5E7EB' }}
            >
              2
            </button>
            <button
              onClick={() => inputDigit('3')}
              className={numberButtonClass}
              style={{ color: '#11194F', borderColor: '#E5E7EB' }}
            >
              3
            </button>
            <button
              onClick={handleEquals}
              className={`${equalsButtonClass} row-span-2 text-2xl`}
              style={{ backgroundColor: '#10B981' }}
            >
              =
            </button>

            {/* Bottom Row */}
            <button
              onClick={() => inputDigit('0')}
              className={`${numberButtonClass} col-span-2`}
              style={{ color: '#11194F', borderColor: '#E5E7EB' }}
            >
              0
            </button>
            <button
              onClick={inputDecimal}
              className={numberButtonClass}
              style={{ color: '#11194F', borderColor: '#E5E7EB' }}
            >
              .
            </button>
          </div>

          {/* Apply Button */}
          <button
            onClick={handleApply}
            className="w-full py-4 rounded-xl font-bold text-lg text-white transition-all hover:scale-105 active:scale-95"
            style={{ backgroundColor: '#FACC15' }}
          >
            Aplicar al Carrito
          </button>
        </div>
      </div>
    </div>
  );
};
